import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IoSparklesOutline,
  IoWarningOutline,
  IoRefreshOutline,
  IoFlashOutline,
  IoTrendingDownOutline,
  IoTrendingUpOutline,
  IoRemoveOutline,
  IoCubeOutline,
} from 'react-icons/io5';
import { api } from '../api';

type Tab = 'return-risk' | 'forecast' | 'actions' | 'competitors' | 'images';

const TAB_LABELS: Record<Tab, string> = {
  'return-risk': 'Риск возврата',
  forecast: 'Прогноз спроса',
  actions: 'Анализ акций',
  competitors: 'Конкуренты',
  images: 'Vision-чек фото',
};

export function AiInsightsPage() {
  const [tab, setTab] = useState<Tab>('return-risk');

  return (
    <>
      <div className="page-header">
        <div>
          <h2><IoSparklesOutline style={{ verticalAlign: 'middle' }} /> AI Insights</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Аналитика и прогнозы на базе локальной нейросети
          </div>
        </div>
      </div>

      <div className="ai-tabs">
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <button
            key={t}
            className={`ai-tab${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'return-risk' && <ReturnRiskPanel />}
      {tab === 'forecast' && <ForecastPanel />}
      {tab === 'actions' && <ActionsAnalysisPanel />}
      {tab === 'competitors' && <CompetitorsPanel />}
      {tab === 'images' && <div className="card empty">Vision-чек фото — в разработке</div>}
    </>
  );
}

function CompetitorsPanel() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['ai-competitor-digest'], queryFn: api.aiCompetitorDigest });
  const recompute = useMutation({
    mutationFn: api.aiCompetitorDigestRecompute,
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['ai-competitor-digest'] }), 30_000),
  });

  const highlightIcon = (t: string) => {
    if (t === 'new_competitor') return '🆕';
    if (t === 'price_outlier') return '⚠️';
    if (t === 'opportunity') return '💡';
    if (t === 'risk') return '🚨';
    return '•';
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="muted" style={{ fontSize: 13 }}>
          Снимок конкурентов из Ozon + анализ изменений · {data.data?.snapshotsCount ?? 0} снимков
        </div>
        <button className="btn ghost" onClick={() => recompute.mutate()} disabled={recompute.isPending}>
          <IoRefreshOutline /> {recompute.isPending ? 'Запущено…' : 'Пересчитать'}
        </button>
      </div>

      {data.isLoading && <div className="card">Загрузка…</div>}
      {data.data && !data.data.latest && (
        <div className="card empty">
          <IoFlashOutline style={{ fontSize: 32, marginBottom: 12 }} />
          <div>Дайджест ещё не построен. Нажмите «Пересчитать».</div>
        </div>
      )}
      {data.data?.latest && (
        <>
          <div className="card" style={{ padding: 20, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}><IoSparklesOutline style={{ verticalAlign: 'middle' }} /> AI-дайджест</h3>
              <span className="muted" style={{ fontSize: 11 }}>
                {new Date(data.data.latest.createdAt).toLocaleString('ru-RU')} · {data.data.latest.model}
              </span>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>{data.data.latest.summary}</div>
          </div>

          {data.data.latest.highlights.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Ключевые наблюдения</h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {data.data.latest.highlights.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 12px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}>
                    <span style={{ fontSize: 16, lineHeight: 1 }}>{highlightIcon(h.type)}</span>
                    <span>{h.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function ActionsAnalysisPanel() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['ai-actions'], queryFn: api.aiActions });
  const recompute = useMutation({
    mutationFn: api.aiActionsRecompute,
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['ai-actions'] }), 60_000),
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="muted" style={{ fontSize: 13 }}>
          Для каждой акции AI оценивает: маржу после скидки, ожидаемый прирост продаж и общую рекомендацию.
        </div>
        <button className="btn ghost" onClick={() => recompute.mutate()} disabled={recompute.isPending}>
          <IoRefreshOutline /> {recompute.isPending ? 'Запущено…' : 'Пересчитать'}
        </button>
      </div>

      {data.isLoading && <div className="card">Загрузка…</div>}
      {data.data && data.data.items.length === 0 && (
        <div className="card empty">
          <IoFlashOutline style={{ fontSize: 32, marginBottom: 12 }} />
          <div>Акций ещё не подгружено. Нажмите «Пересчитать» — AI разберёт каждую (~30 сек/акция).</div>
        </div>
      )}
      {data.data && data.data.items.length > 0 && (
        <div style={{ display: 'grid', gap: 12 }}>
          {data.data.items.map((a) => {
            const verdictBadge = a.aiVerdict === 'recommended' ? 'badge-success'
              : a.aiVerdict === 'caution' ? 'badge-warn'
              : a.aiVerdict === 'avoid' ? 'badge-error' : 'badge';
            const verdictLabel = a.aiVerdict === 'recommended' ? '✓ Рекомендуем'
              : a.aiVerdict === 'caution' ? '⚠ Осторожно'
              : a.aiVerdict === 'avoid' ? '✗ Не участвовать'
              : 'AI анализ не готов';
            return (
              <div key={a.id} className="card" style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0, fontSize: 16 }}>{a.title}</h3>
                      <span className={`badge ${verdictBadge}`}>{verdictLabel}</span>
                      {a.isParticipating && <span className="badge badge-info">Участвуем</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      Тип: {a.actionType}
                      {a.discountValue != null && ` · скидка ${a.discountValue} ${a.discountType === 'PERCENT' ? '%' : '₽'}`}
                      {' · '}
                      {new Date(a.dateStart).toLocaleDateString('ru-RU')} — {new Date(a.dateEnd).toLocaleDateString('ru-RU')}
                    </div>
                    {a.aiAnalysis && (
                      <div style={{ marginTop: 12, padding: 12, background: 'var(--accent-soft)', borderRadius: 8, fontSize: 13, lineHeight: 1.5 }}>
                        <IoSparklesOutline style={{ verticalAlign: 'middle' }} /> {a.aiAnalysis}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', minWidth: 160 }}>
                    {a.expectedMarginPct != null && (
                      <div style={{ textAlign: 'right' }}>
                        <div className="muted" style={{ fontSize: 11 }}>Ожид. маржа</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: a.expectedMarginPct < 0 ? 'var(--red)' : a.expectedMarginPct < 5 ? '#f59e0b' : 'var(--green)' }}>
                          {a.expectedMarginPct > 0 ? '+' : ''}{a.expectedMarginPct.toFixed(0)}%
                        </div>
                      </div>
                    )}
                    {a.expectedUpliftFactor != null && (
                      <div style={{ textAlign: 'right' }}>
                        <div className="muted" style={{ fontSize: 11 }}>Прирост продаж</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>×{a.expectedUpliftFactor.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="muted" style={{ marginTop: 12, fontSize: 11, display: 'flex', gap: 16 }}>
                  <span>Участвует наших: <strong style={{ color: 'var(--accent)' }}>{a.participatingCount}</strong></span>
                  <span>Можем добавить: <strong style={{ color: 'var(--green)' }}>{a.potentialCount}</strong></span>
                  {a.bannedCount > 0 && <span>Заблокировано: <strong style={{ color: 'var(--red)' }}>{a.bannedCount}</strong></span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function ForecastPanel() {
  const [filter, setFilter] = useState<'stockout' | 'low' | 'trending_up' | 'trending_down' | 'all'>('stockout');
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ['ai-forecasts', filter],
    queryFn: () => api.aiForecasts({ filter, limit: 200 }),
  });
  const recompute = useMutation({
    mutationFn: api.aiForecastsRecompute,
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['ai-forecasts'] }), 15_000),
  });

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`status-tab${filter === 'stockout' ? ' active' : ''}`} onClick={() => setFilter('stockout')}>
            <IoWarningOutline /> Risk stock-out ({data.data?.summary.stockout ?? 0})
          </button>
          <button className={`status-tab${filter === 'low' ? ' active' : ''}`} onClick={() => setFilter('low')}>
            <IoCubeOutline /> Мало остатков ≤14д ({data.data?.summary.low ?? 0})
          </button>
          <button className={`status-tab${filter === 'trending_up' ? ' active' : ''}`} onClick={() => setFilter('trending_up')}>
            <IoTrendingUpOutline /> Растёт спрос ({data.data?.summary.trendingUp ?? 0})
          </button>
          <button className={`status-tab${filter === 'trending_down' ? ' active' : ''}`} onClick={() => setFilter('trending_down')}>
            <IoTrendingDownOutline /> Падает ({data.data?.summary.trendingDown ?? 0})
          </button>
          <button className={`status-tab${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>
            Все ({data.data?.summary.total ?? 0})
          </button>
        </div>
        <button className="btn ghost" style={{ marginLeft: 'auto' }} onClick={() => recompute.mutate()} disabled={recompute.isPending}>
          <IoRefreshOutline /> {recompute.isPending ? 'Запущено…' : 'Пересчитать'}
        </button>
      </div>

      {data.isLoading && <div className="card">Загрузка…</div>}
      {data.data && data.data.items.length === 0 && (
        <div className="card empty">
          <IoFlashOutline style={{ fontSize: 32, marginBottom: 12 }} />
          <div>Прогноз ещё не построен. Нажмите «Пересчитать».</div>
        </div>
      )}
      {data.data && data.data.items.length > 0 && (
        <div className="card">
          <table className="products">
            <thead>
              <tr>
                <th>Товар</th>
                <th className="num">Прод./день (7д)</th>
                <th className="num">Прод./день (30д)</th>
                <th>Тренд</th>
                <th className="num">Прогноз 7д</th>
                <th className="num">Остаток</th>
                <th className="num">Дней хватит</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {data.data.items.map((f) => (
                <tr key={f.productId}>
                  <td style={{ fontSize: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {f.image && <img src={f.image} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380 }}>{f.name ?? '—'}</div>
                        <div className="muted" style={{ fontSize: 11 }}>{f.brand} · {f.offerId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="num">{f.avgDaily7.toFixed(1)}</td>
                  <td className="num">{f.avgDaily30.toFixed(1)}</td>
                  <td>
                    {f.trend === 'up' && <span style={{ color: 'var(--green)' }}><IoTrendingUpOutline /> рост</span>}
                    {f.trend === 'down' && <span style={{ color: 'var(--red)' }}><IoTrendingDownOutline /> падение</span>}
                    {f.trend === 'flat' && <span className="muted"><IoRemoveOutline /> ровно</span>}
                  </td>
                  <td className="num"><strong>{f.predicted7d}</strong></td>
                  <td className="num">{f.stockNow}</td>
                  <td className="num">
                    {f.daysOfStock != null ? (
                      <span style={{ color: f.daysOfStock < 7 ? 'var(--red)' : f.daysOfStock < 14 ? '#f59e0b' : 'var(--text)' }}>
                        {f.daysOfStock.toFixed(1)}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {f.stockoutRisk ? (
                      <span className="badge badge-error"><IoWarningOutline /> Закончится</span>
                    ) : (
                      <span className="badge badge-success">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ReturnRiskPanel() {
  const [level, setLevel] = useState<'high' | 'medium' | 'low' | ''>('high');
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ['ai-return-risks', level],
    queryFn: () => api.aiReturnRisks({ level: level || undefined, limit: 100 }),
  });
  const recompute = useMutation({
    mutationFn: api.aiReturnRisksRecompute,
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ['ai-return-risks'] }), 30_000);
    },
  });

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`status-tab${level === 'high' ? ' active' : ''}`} onClick={() => setLevel('high')}>
            <IoWarningOutline /> Высокий ({data.data?.summary.find((s) => s.level === 'high')?.count ?? 0})
          </button>
          <button className={`status-tab${level === 'medium' ? ' active' : ''}`} onClick={() => setLevel('medium')}>
            Средний ({data.data?.summary.find((s) => s.level === 'medium')?.count ?? 0})
          </button>
          <button className={`status-tab${level === 'low' ? ' active' : ''}`} onClick={() => setLevel('low')}>
            Низкий ({data.data?.summary.find((s) => s.level === 'low')?.count ?? 0})
          </button>
          <button className={`status-tab${level === '' ? ' active' : ''}`} onClick={() => setLevel('')}>Все</button>
        </div>
        <button
          className="btn ghost"
          style={{ marginLeft: 'auto' }}
          onClick={() => recompute.mutate()}
          disabled={recompute.isPending}
        >
          <IoRefreshOutline /> {recompute.isPending ? 'Запущено…' : 'Пересчитать'}
        </button>
      </div>

      {data.isLoading && <div className="card">Загрузка…</div>}
      {data.error && <div className="card" style={{ color: 'var(--red)' }}>{(data.error as Error).message}</div>}

      {data.data && data.data.items.length === 0 && (
        <div className="card empty">
          <IoFlashOutline style={{ fontSize: 32, marginBottom: 12 }} />
          <div>Risk-score не рассчитан. Нажмите «Пересчитать», займёт ~30 сек.</div>
        </div>
      )}

      {data.data && data.data.items.length > 0 && (
        <div className="card">
          <table className="products">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Score</th>
                <th style={{ width: 90 }}>Уровень</th>
                <th>Товар</th>
                <th className="num">Продано 90д</th>
                <th className="num">Возвратов</th>
                <th className="num">% возвратов</th>
                <th>Рейтинг</th>
                <th>Топ-причины</th>
              </tr>
            </thead>
            <tbody>
              {data.data.items.map((r) => (
                <tr key={r.productId}>
                  <td>
                    <span className={`risk-score risk-${r.level}`}>{r.score}</span>
                  </td>
                  <td>
                    <span className={`badge risk-badge-${r.level}`}>
                      {r.level === 'high' ? 'Высокий' : r.level === 'medium' ? 'Средний' : 'Низкий'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {r.image && <img src={r.image} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380 }}>{r.name ?? '—'}</div>
                        <div className="muted" style={{ fontSize: 11 }}>
                          {r.brand} · {r.offerId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="num">{r.salesCount90d}</td>
                  <td className="num">{r.returnsCount90d}</td>
                  <td className="num">
                    {r.returnRate != null ? `${(r.returnRate * 100).toFixed(1)}%` : '—'}
                  </td>
                  <td>
                    {r.avgRating != null ? (
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        ★ {r.avgRating.toFixed(2)}
                        {r.negativeReviews > 0 && (
                          <span className="muted" style={{ fontSize: 11 }}>
                            <IoTrendingDownOutline style={{ color: 'var(--red)', verticalAlign: 'middle' }} /> {r.negativeReviews}
                          </span>
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {r.topCategories.slice(0, 2).map((c, i) => (
                      <div key={i}>
                        <strong>{c.category}</strong> ({c.count})
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
